import { useCallback, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";

// Material UI Styling
import { useTheme } from "@mui/styles";

// Material UI Form Components
import { Grid, Theme, Typography } from "@mui/material/";
import { CheckboxField } from "src/components/FormikComponents/CheckboxField";
import { TextField } from "src/components/FormikComponents/TextField";

// Interfaces
import {
  IPairingPositionCounts,
  IPlannedCreditMap,
  IPosition,
  setPlannedCreditInMap,
} from "@vmosolutions/oc-interfaces";
import { isEqual } from "lodash";
import { IRootState } from "src/redux/reducers/CombinedReducers";

import { PositionBadge } from "src/components/CrewSchedule/PositionBadge";

interface IBulkPairingInfo {
  initialPairingPosCounts?: IPairingPositionCounts;
  pairingPosCounts: IPairingPositionCounts;
  setPairingPosCounts: (posCounts: IPairingPositionCounts) => any;
  recurringFlight?: boolean;
  disabled?: boolean;
  hideCheckbox?: boolean;
  initialIncludeSplits?: boolean;
  includeSplits?: boolean;
  setIncludeSplits?: (value: boolean) => void;
  hideSplitsCheckbox?: boolean;
  initialIsBiddable?: boolean;
  isBiddable?: boolean;
  setIsBiddable?: (value: boolean) => void;
  initialPlannedCredit?: number;
  plannedCredit?: number;
  setPlannedCredit?: (value: number) => void;
  initialPlannedCreditMap?: IPlannedCreditMap;
  plannedCreditMap?: IPlannedCreditMap;
  setPlannedCreditMap?: (value: IPlannedCreditMap) => void;
  hidePlannedCredit?: boolean;
}

const BulkPairingInfoStyles = (theme: Theme): any => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-end",
  "& .build_control": {
    display: "flex",
    "& .start_date, & .until_date": {
      alignSelf: "center",
      "& .input_control label": { width: "auto" },
    },
  },
});

// If user chooses to create a pairing from pending flights, assigned default crew slots counts.
const defaultValidValue = { CA: 1, FO: 1 };

/**
 * Renders all fields used to determined whether or not a particular new flight(s) should create a pairing.
 * @param props -
 */
export const BulkPairingInfo = (props: IBulkPairingInfo): JSX.Element => {
  const theme: Theme = useTheme();

  // Available Positions
  const positions: IPosition[] = useSelector((state: IRootState) => state.structures.customerStructure.positions).sort(
    (a, b) => (b.displayOrder > a.displayOrder ? -1 : 1)
  );

  const {
    initialPairingPosCounts,
    pairingPosCounts,
    setPairingPosCounts,
    recurringFlight,
    disabled,
    hideCheckbox,
    hideSplitsCheckbox,
    initialIncludeSplits,
    includeSplits,
    setIncludeSplits,
    initialIsBiddable,
    isBiddable,
    setIsBiddable,
    initialPlannedCredit,
    plannedCredit,
    setPlannedCredit,
    initialPlannedCreditMap,
    plannedCreditMap,
    setPlannedCreditMap,
    hidePlannedCredit,
  } = props;

  /**
   * Set new desired position counts on pairing created from pending flight(s)
   */
  const handleSetPositionCount = useCallback(
    (pos: string, value: number) => {
      const updatePositionCounts = { ...pairingPosCounts, [pos]: value };
      for (const key in updatePositionCounts) {
        if (updatePositionCounts[key] < 1) {
          // If the position count is zero, just remove the pos from the object.
          delete updatePositionCounts[key];
        }
      }
      // If object has no position counts, return null;
      setPairingPosCounts(Object.keys(updatePositionCounts).length === 0 ? null : updatePositionCounts);
    },
    [pairingPosCounts, setPairingPosCounts]
  );

  return (
    <Grid container sx={BulkPairingInfoStyles(theme)} alignItems="start">
      {!hideCheckbox && (
        <Grid item>
          <CheckboxField
            className="field create_pairing_checkbox"
            label={recurringFlight ? "Create Pairing(s) from recurring pattern" : "Create Pairing from pending flights"}
            name="createPairing"
            checked={!!pairingPosCounts}
            onChange={() => {
              const newValue = !pairingPosCounts ? defaultValidValue : null;
              setPairingPosCounts(newValue);
              if (!newValue) {
                //clear includeSplits if you deselect making pairing from flights
                setIncludeSplits(false);
                setIsBiddable(false);
                setPlannedCredit(0);
              }
            }}
            disabled={disabled}
            meta={{
              value: pairingPosCounts,
              touched: !isEqual(pairingPosCounts, initialPairingPosCounts),
              initialValue: initialPairingPosCounts,
            }}
          />
        </Grid>
      )}

      {!hideSplitsCheckbox && (
        <Grid item>
          <CheckboxField
            className="field create_pairing_checkbox"
            label={"Include Splits(where possible)"}
            name="includeSplits"
            checked={includeSplits}
            onChange={(event) => {
              setIncludeSplits(!includeSplits);
            }}
            disabled={disabled || !pairingPosCounts}
            meta={{
              value: includeSplits,
              touched: includeSplits !== initialIncludeSplits,
              initialValue: initialIncludeSplits,
            }}
          />
        </Grid>
      )}
      {!hidePlannedCredit && (
        <Grid item>
          <TextField
            className="plannedCredit_textfield split_label_input"
            label="Planned Credit"
            name="plannedCredit"
            type="number"
            value={plannedCredit ?? 0}
            onChange={(event) => {
              const newCredit = event?.target?.value ? parseFloat(event?.target?.value) : 0;
              setPlannedCredit(newCredit);
            }}
            disabled={disabled || !pairingPosCounts}
            fullWidth={false}
            meta={{
              value: plannedCredit,
              touched: plannedCredit !== initialPlannedCredit,
              initialValue: initialPlannedCredit,
            }}
          />
        </Grid>
      )}
      {!!initialPlannedCreditMap &&
        Object.keys(initialPlannedCreditMap).map((key) => {
          const initialCredit = initialPlannedCreditMap?.[key]?.credit ?? 0;
          const currentCredit = plannedCreditMap?.[key]?.credit ?? 0;
          const displayName = plannedCreditMap?.[key]?.displayName ?? key;
          return (
            <Grid item key={key}>
              <TextField
                className="plannedCredit_textfield split_label_input"
                label={displayName}
                name={key}
                type="number"
                value={plannedCreditMap?.[key]?.credit}
                onChange={(event) => {
                  const newCredit = event?.target?.value ? parseFloat(event?.target?.value) : 0;
                  const newCreditMap = setPlannedCreditInMap(plannedCreditMap, key, newCredit);
                  setPlannedCreditMap(newCreditMap);
                }}
                disabled={disabled || !pairingPosCounts}
                fullWidth={false}
                meta={{
                  value: currentCredit,
                  touched: currentCredit !== initialCredit,
                  initialValue: initialCredit,
                }}
              />
            </Grid>
          );
        })}
      <Grid item xs={12}>
        <Typography className="crew_position_count_label" sx={{ marginLeft: "5px" }} variant="body1">
          Pairing Crew Slots
        </Typography>
      </Grid>
      <Grid item>
        <Grid container className="crewslot_fields" style={{ width: "auto", paddingLeft: 10 }}>
          {positions.map((pos, index) => {
            const count = pairingPosCounts?.[pos.title] ?? 0;
            const initialCount = initialPairingPosCounts?.[pos.title] ?? 0;
            return (
              <Grid item key={index} className={`${pos.title} position_count`}>
                <Grid container alignItems={"center"}>
                  <Grid item>
                    <PositionBadge positionTitle={pos.title} filled={count > 0} />
                  </Grid>
                  <Grid>
                    <TextField
                      className={"field textfield "}
                      // label={pos.description}
                      type="number"
                      value={count}
                      InputProps={{ inputProps: { min: 0, max: 10 } }}
                      onChange={(event) => {
                        const newCount = event?.target?.value ? parseInt(event?.target?.value) : 0;
                        handleSetPositionCount(pos.title, newCount);
                      }}
                      disabled={disabled}
                      meta={{
                        value: count,
                        touched: count !== initialCount,
                        initialValue: initialCount,
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>
            );
          })}
        </Grid>
      </Grid>
    </Grid>
  );
};
